import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * SignIn — dedicated sign-in page for returning customers.
 * Same magic-link flow as the home page, but with no marketing.
 * Shareable URL: /signin
 */
export default function SignIn() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.post("/auth/request-link", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't send the link, try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center px-6 py-16 surface-tint">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="mono-caps hover:text-slate-900 transition-colors mb-8 inline-block"
          data-testid="signin-back-home"
        >
          ← The Gauntlet Market Assessment Tool
        </Link>

        <h1 className="font-display text-4xl sm:text-5xl mb-3">Sign in.</h1>
        <p className="text-slate-500 mb-10 leading-relaxed">
          Enter the email you used before. We'll send a one-time sign-in link — expires in 20 minutes.
        </p>

        {!sent ? (
          <form onSubmit={submit} className="space-y-6" data-testid="signin-form">
            <div>
              <label className="mono-caps block mb-3">Email</label>
              <input
                data-testid="signin-email-input"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="input-line"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600 font-medium" data-testid="signin-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading || !email.trim()}
              data-testid="signin-submit-button"
              className="btn-primary w-full"
            >
              {loading ? "Sending…" : "Send sign-in link"}
            </button>

            <div className="text-sm text-slate-500 pt-4 border-t border-slate-200">
              First time here?{" "}
              <Link to="/" className="text-blue-700 hover:text-blue-900 font-medium" data-testid="signin-first-time-link">
                Learn about The Gauntlet →
              </Link>
            </div>
          </form>
        ) : (
          <div data-testid="signin-sent-message" className="card-tint">
            <div className="mono-caps ring-accent-1 mb-4">Link dispatched</div>
            <p className="text-slate-800 leading-relaxed">
              Check <span className="font-semibold">{email}</span>. The sign-in link
              expires in 20 minutes and can only be used once.
            </p>
            <p className="text-slate-500 text-sm mt-4">
              Didn't get it? Check spam, then{" "}
              <button
                type="button"
                onClick={() => { setSent(false); setEmail(""); }}
                className="text-blue-700 hover:text-blue-900 font-medium underline"
                data-testid="signin-resend"
              >
                request a new one
              </button>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
